'use strict';

import { CONFIG } from '../core/config.js';

/**
 * Generic Events Manager
 * Handles event binding, delegation, routing, and cleanup for the search filter form system
 */
export class EventsManager {
	constructor(handlers, forms, options = {}) {
		this.handlers = handlers;
		this.forms = forms;
		this.options = {
			passive: true,
			capture: false,
			...options,
		};

		// Track bound event listeners for cleanup
		this.boundListeners = new Map();

		this.eventConfig = {
			blur: {
				handler: this.handleBlur.bind(this),
				options: { passive: true, capture: true },
			},
			click: {
				handler: this.handleGlobalClick.bind(this),
				options: { passive: false, capture: false },
			},
			change: {
				handler: this.handleInputChange.bind(this),
				options: { passive: true, capture: false },
			},
			submit: {
				handler: this.handleFormSubmit.bind(this),
				options: { passive: false, capture: false },
			},
			input: {
				handler: this.handleInput.bind(this),
				options: { passive: true, capture: false },
			},
			// Not passive: arrow keys and Escape are intercepted for dropdown
			// navigation, so the handler needs preventDefault().
			keydown: {
				handler: this.handleKeydown.bind(this),
				options: { passive: false, capture: false },
			},
		};
	}

	bind() {
		// Bind form-specific events to each search filter form
		this.forms.forEach((form, formIndex) => {
			const formListeners = new Map();

			Object.entries(this.eventConfig).forEach(([eventType, config]) => {
				if (eventType !== 'click') {
					// Skip click as it's handled globally
					form.addEventListener(eventType, config.handler, config.options);
					formListeners.set(eventType, {
						handler: config.handler,
						options: config.options,
					});
				}
			});

			this.boundListeners.set(`form_${formIndex}`, formListeners);
		});

		document.addEventListener(
			'click',
			this.eventConfig.click.handler,
			this.eventConfig.click.options,
		);
		this.boundListeners.set('global_click', {
			handler: this.eventConfig.click.handler,
			options: this.eventConfig.click.options,
		});
	}

	unbind() {
		this.boundListeners.forEach((listeners, key) => {
			if (key === 'global_click') {
				document.removeEventListener(
					'click',
					listeners.handler,
					listeners.options,
				);
			} else {
				const formIndex = parseInt(key.replace('form_', ''));
				const form = this.forms[formIndex];
				if (form) {
					listeners.forEach((listenerConfig, eventType) => {
						form.removeEventListener(
							eventType,
							listenerConfig.handler,
							listenerConfig.options,
						);
					});
				}
			}
		});
		this.boundListeners.clear();
	}

	handleGlobalClick(e) {
		const target = e.target;

		const clickTargets = [
			{
				selector: CONFIG.ELEMENTS.closeButton,
				handler: 'closeButton',
				preventDefault: true,
			},
			{
				selector: CONFIG.ELEMENTS.deselectButton,
				handler: 'deselectButton',
				preventDefault: true,
			},
			{
				selector: CONFIG.ELEMENTS.modalTrigger,
				handler: 'modalTrigger',
				preventDefault: true,
			},
			{
				selector: CONFIG.ELEMENTS.input,
				handler: 'inputClick',
			},
			{ selector: CONFIG.ELEMENTS.tabbedOption, handler: 'tabbedOption' },
		];

		for (const { selector, handler, preventDefault } of clickTargets) {
			const element = target.closest(selector);
			if (element) {
				if (preventDefault) {
					e.preventDefault();
					e.stopPropagation();
				}
				this.handlers[handler]?.(e);
				return;
			}
		}

		this.handlers.clickOutside?.(e);
	}

	handleInputChange(e) {
		const input = e.target;

		if (input.matches('input[type="checkbox"]')) {
			this.handlers.checkboxChange?.(e);
			return;
		}

		// Radios are routed off `change` rather than `click` so that arrow-key
		// selection within a radio group is covered, which does not reliably
		// produce a click event.
		if (input.matches('input[type="radio"]')) {
			this.handlers.radioChange?.(e);
		}
	}

	handleFormSubmit(e) {
		this.handlers.formSubmit?.(e);
	}

	handleInput(e) {
		this.handlers.input?.(e);
	}

	handleKeydown(e) {
		this.handlers.keydown?.(e);
	}

	handleBlur(e) {
		this.handlers.blur?.(e);
	}
}
